/**
 * /suprimentos/materiais-a-cadastrar — a fila única de material que ainda
 * não existe no Cadastro de Suprimentos (ADR-0068, 2026-09-12).
 *
 * Substitui "Produtos pendentes" e "Solicitações de cadastro": a Solicitação
 * é a verdade; o produto pendente que veio do projeto, da planilha ou do
 * revisor é só o rascunho ancorado a ela (`produto_dados`). Cadastrar usa o
 * MESMO formulário do Cadastro de Suprimentos — não existe um segundo.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertCircle, FiPlus, FiRefreshCw, FiUpload } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import AsyncButton from "../../../components/AsyncButton";
import ConfirmDialog from "../../../components/ConfirmDialog";
import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import useMediaQuery from "../../../hooks/useMediaQuery";
import { aplicarTextura, listarTiposSuprimento } from "../../../services/estoque";
import { obterItemsVinculados } from "../../../services/estoquePendentes";
import {
  cadastrarDeSolicitacao, cancelarSolicitacaoCadastro, criarSolicitacaoCadastro,
  ESTADO_SC_LABEL, ESTADO_SC_TONE, ESTADOS_SOLIC_CADASTRO, iniciarAnaliseSolicitacaoCadastro,
  linhasDe, listarSolicitacoesCadastro, rejeitarSolicitacaoCadastro,
} from "../../../services/suprimentos";
import { mq } from "../../../styles/canonical/breakpoints";
import { fmtData } from "../helpers";
import FormularioProduto, { inteiroTexto } from "../Produtos/FormularioProduto";
import CardsMateriais from "./CardsMateriais";
import {
  Card, ErrorMsg, Field, Form, FormGrid, InlineHint, Input, Pill,
  PrimaryButton, SecondaryButton, Select, Table, TableWrapper, Textarea, TruncCell,
} from "../styles";
import { AbaBtn, AbasWrap } from "../Sobras/styles";
import { Carregando, Vazio } from "../_shared/Estados";

const NOVA_VAZIA = { nome: "", chave_externa: "", tipo_material: "OUTRO", codigo_sugerido: "" };
const FORM_PEDIR_ID = "form-pedir-cadastro";

/** Origens na ordem em que aparecem como abas. "" = todas. */
const FONTES = [
  ["", "Todas"], ["PROJETO", "Projeto"], ["PLANILHA", "Planilha"], ["PHARUCUT", "PharuCut"],
  ["PHARU3D", "Pharu 3D"], ["REVISOR", "Ferragens"], ["CAD", "Importação CAD"], ["MANUAL", "Manual"],
];

/**
 * O que abre no formulário de produto: o rascunho ancorado (quando veio do
 * projeto/planilha/revisor) ou a sugestão da origem (payload). Nada disso é
 * definitivo: vale o que o almoxarife confirmar.
 */
function rascunhoDe(sol, tipos) {
  const p = sol.produto_dados;
  const payload = sol.payload || {};
  const tipoSugerido = p?.tipo_suprimento || payload.tipo_suprimento
    || (sol.tipo_material === "CHAPA" ? "MDF_BP" : sol.tipo_material === "FITA" ? "FITA_PVC" : "");
  const entrada = tipos.find((t) => t.codigo === tipoSugerido);
  return {
    // "PEND-..." e placeholder do rascunho, nunca sugestao de codigo.
    codigo: [sol.codigo_sugerido, payload.codigo, p?.codigo].find((c) => c && !String(c).startsWith("PEND-")) || "",
    nome: p?.nome || sol.nome,
    descricao: "",
    unidade: p?.unidade || entrada?.unidade || (sol.tipo_material === "CHAPA" ? "CH" : sol.tipo_material === "FITA" ? "M" : "UN"),
    categoria: p?.categoria || entrada?.categoria || "OUTRO",
    custo_medio: p && Number(p.custo_medio) > 0 ? Number(p.custo_medio) : (payload.custo_medio ? Number(payload.custo_medio) : null),
    estoque_minimo: "0",
    ativo: true,
    m2_por_unidade: p?.m2_por_unidade || "",
    largura_mm: inteiroTexto(p?.largura_mm || payload.largura_mm),
    comprimento_mm: inteiroTexto(p?.comprimento_mm || payload.comprimento_mm),
    espessura_mm: inteiroTexto(p?.espessura_mm || payload.espessura_mm),
    tem_veio: p?.tem_veio ?? payload.tem_veio ?? null,
    tipo_suprimento: tipoSugerido,
    atributos: { ...(payload.atributos || {}), ...(payload.cor ? { cor: payload.cor } : {}), ...(p?.atributos || {}) },
    fabricante: p?.fabricante || payload.fabricante || "",
    referencia_fabricante: "",
    ncm: p?.ncm || payload.ncm || "",
  };
}

export default function MateriaisACadastrar() {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const pode = (p) => isMaster || hasAllPermissions([p]);
  const podeCriar = pode("solicitacao_cadastro:create");
  const podeAprovar = pode("solicitacao_cadastro:aprovar");
  const mobile = useMediaQuery(mq.mobile);

  const [linhas, setLinhas] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [estado, setEstado] = useState("PENDENTE,EM_ANALISE");
  const [fonte, setFonte] = useState("");
  const [busca, setBusca] = useState("");
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [nova, setNova] = useState(null);
  const [decidindo, setDecidindo] = useState(null);
  const [editando, setEditando] = useState(null);
  const [vinculados, setVinculados] = useState(null);
  const [rejeitando, setRejeitando] = useState(null);
  const [motivo, setMotivo] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      setLinhas(linhasDe(await listarSolicitacoesCadastro(request, { estado })));
      setErro(null);
    } catch (err) {
      setErro(err?.payload?.detalhe || err?.message || "Erro ao carregar.");
    } finally {
      setLoading(false);
    }
  }, [request, estado]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    listarTiposSuprimento(request).then((t) => setTipos(t?.tipos || [])).catch(() => setTipos([]));
  }, [request]);

  const porFonte = useMemo(() => {
    const c = {};
    linhas.forEach((l) => { c[l.fonte] = (c[l.fonte] || 0) + 1; });
    return c;
  }, [linhas]);

  const visiveis = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return linhas.filter((l) => (!fonte || l.fonte === fonte) && (!q || [l.nome, l.chave_externa, l.produto_codigo].some((v) => (v || "").toLowerCase().includes(q))));
  }, [linhas, fonte, busca]);

  const kpis = useMemo(() => [
    { label: "Pendentes", value: linhas.filter((l) => l.estado === "PENDENTE").length, tone: "warning" },
    { label: "Em análise", value: linhas.filter((l) => l.estado === "EM_ANALISE").length, tone: "info" },
    { label: "Com sobras presas", value: linhas.filter((l) => l.sobras_vinculadas > 0).length, tone: linhas.some((l) => l.sobras_vinculadas > 0) ? "warning" : "neutral" },
  ], [linhas]);

  const tipoAtual = useMemo(
    () => tipos.find((t) => t.codigo === editando?.tipo_suprimento),
    [tipos, editando?.tipo_suprimento],
  );

  const abrirCadastro = async (sol) => {
    setDecidindo(sol);
    setEditando(rascunhoDe(sol, tipos));
    setVinculados(null);
    if (sol.produto) {
      try { setVinculados(await obterItemsVinculados(request, sol.produto)); } catch { setVinculados(null); }
    }
  };

  const fecharCadastro = () => { setDecidindo(null); setEditando(null); setVinculados(null); };

  const salvarCadastro = async (e) => {
    e.preventDefault();
    if (!editando.codigo?.trim()) { toast.error("Informe o código do produto."); return; }
    if (!editando.nome?.trim()) { toast.error("Informe o nome do produto."); return; }
    const corpo = {
      codigo: editando.codigo.trim(),
      nome: editando.nome.trim(),
      tipo_suprimento: editando.tipo_suprimento || "",
      unidade: editando.unidade || "",
      custo_medio: editando.custo_medio === null || editando.custo_medio === undefined || editando.custo_medio === "" ? null : String(editando.custo_medio),
      largura_mm: editando.largura_mm || null,
      comprimento_mm: editando.comprimento_mm || null,
      espessura_mm: editando.espessura_mm || null,
      tem_veio: editando.tem_veio ?? null,
      fabricante: editando.fabricante || "",
      ncm: editando.ncm || "",
      atributos: editando.atributos || {},
    };
    try {
      const sol = await cadastrarDeSolicitacao(request, decidindo.id, corpo);
      try {
        await aplicarTextura(request, sol?.produto, editando);
      } catch (errTextura) {
        toast.error(`Material cadastrado, mas a textura não foi enviada: ${errTextura?.payload?.detalhe || errTextura?.message || "erro"}`);
      }
      toast.success(`${corpo.nome} cadastrado.${decidindo.sobras_vinculadas ? ` ${decidindo.sobras_vinculadas} sobra(s) liberada(s).` : ""}`);
      fecharCadastro();
      await carregar();
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Erro ao cadastrar.");
    }
  };

  const handleNova = async (e) => {
    e.preventDefault();
    if (!nova.nome) { setErro("Informe o nome do material."); return; }
    setErro(null);
    try {
      await criarSolicitacaoCadastro(request, {
        nome: nova.nome,
        chave_externa: nova.chave_externa || `MANUAL-${Date.now()}`,
        tipo_material: nova.tipo_material,
        codigo_sugerido: nova.codigo_sugerido || "",
      });
      setNova(null);
      await carregar();
    } catch (err) {
      setErro(err?.payload?.detalhe || err?.message || "Erro ao pedir cadastro.");
    }
  };

  // Ações de linha (tabela e cards usam as mesmas). Erro de API vira toast
  // automático do `request`; aqui só o sucesso e o recarregar.
  const analisar = async (l) => { await iniciarAnaliseSolicitacaoCadastro(request, l.id); await carregar(); };
  const cancelarPedido = async (l) => {
    await cancelarSolicitacaoCadastro(request, l.id);
    toast.success("Pedido cancelado.");
    await carregar();
  };
  const pedirRejeicao = (l) => { setRejeitando(l); setMotivo(""); };

  const confirmarRejeicao = async () => {
    if (!motivo.trim()) { toast.error("Informe o motivo."); return; }
    try {
      await rejeitarSolicitacaoCadastro(request, rejeitando.id, motivo.trim());
      toast.success("Pedido rejeitado.");
      setRejeitando(null);
      setMotivo("");
      await carregar();
    } catch (err) {
      toast.error(err?.payload?.detalhe || err?.message || "Erro ao rejeitar.");
    }
  };

  return (
    <>
      <EntityHeader
        title="Materiais a cadastrar"
        subtitle="Tudo que chegou de projeto, planilha, ferragens, PharuCut ou pedido da equipe e ainda não existe no Cadastro de Suprimentos. Enquanto espera, entra no orçamento com custo zero e as sobras que vieram junto ficam invisíveis."
        primaryAction={podeCriar ? { label: "Pedir cadastro", icon: <FiPlus size={14} />, onClick: () => setNova({ ...NOVA_VAZIA }) } : undefined}
        actions={[
          { label: "Importar planilha", icon: <FiUpload size={14} />, onClick: () => navigate("/administracao/importacoes") },
          { label: "Atualizar", icon: <FiRefreshCw size={14} />, onClick: carregar },
        ]}
      />
      <KpiStrip items={kpis} />

      <AbasWrap role="tablist" aria-label="Origem do pedido">
        {FONTES.filter(([v]) => !v || porFonte[v]).map(([v, label]) => (
          <AbaBtn key={v || "todas"} role="tab" type="button" aria-selected={fonte === v} $on={fonte === v} onClick={() => setFonte(v)}>
            {label}{v ? ` (${porFonte[v] || 0})` : ` (${linhas.length})`}
          </AbaBtn>
        ))}
      </AbasWrap>

      <Card>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          <Input type="search" placeholder="Buscar por nome, referência ou código…" value={busca} onChange={(e) => setBusca(e.target.value)} style={mobile ? { flex: "1 1 100%", minHeight: 44 } : { minWidth: 240 }} />
          <Select value={estado} onChange={(e) => setEstado(e.target.value)}>
            <option value="PENDENTE,EM_ANALISE">Abertos</option>
            {ESTADOS_SOLIC_CADASTRO.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            <option value="">Todos</option>
          </Select>
        </div>
        {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}
        {loading ? <Carregando /> : visiveis.length === 0 ? (
          <Vazio titulo="Nada esperando cadastro neste filtro" descricao="Material novo que chegar de projeto, planilha, ferragens ou PharuCut aparece aqui sozinho." />
        ) : mobile ? (
          <CardsMateriais
            linhas={visiveis}
            podeAprovar={podeAprovar}
            podeCriar={podeCriar}
            onAnalisar={analisar}
            onCadastrar={abrirCadastro}
            onRejeitar={pedirRejeicao}
            onCancelar={cancelarPedido}
          />
        ) : (
          <TableWrapper>
            <Table>
              <colgroup>
                <col style={{ width: "32%" }} /><col style={{ width: "12%" }} /><col style={{ width: "9%" }} />
                <col style={{ width: "8%" }} /><col style={{ width: "12%" }} /><col style={{ width: "27%" }} />
              </colgroup>
              <thead><tr><th>Material</th><th>Origem</th><th>Tipo</th><th>Sobras</th><th>Situação</th><th>Ações</th></tr></thead>
              <tbody>
                {visiveis.map((l) => (
                  <tr key={l.id}>
                    <td title={l.nome}>
                      <TruncCell>{l.nome}</TruncCell>
                      <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
                        {fmtData(l.created_at)}{l.produto_codigo ? ` · ${l.produto_codigo}` : ""}{l.chave_externa && !l.chave_externa.startsWith("MANUAL-") ? ` · ref. ${l.chave_externa}` : ""}{l.motivo_rejeicao ? ` · ${l.motivo_rejeicao}` : ""}
                      </div>
                    </td>
                    <td><TruncCell>{l.fonte_display || l.fonte}</TruncCell></td>
                    <td>{l.tipo_material}</td>
                    <td>{l.sobras_vinculadas || 0}</td>
                    <td><Pill $tone={ESTADO_SC_TONE[l.estado]}>{ESTADO_SC_LABEL[l.estado] || l.estado_display}</Pill></td>
                    <td>
                      {["PENDENTE", "EM_ANALISE"].includes(l.estado) && (
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {podeAprovar && l.estado === "PENDENTE" && (
                            <AsyncButton variant="secondary" silentSuccess onClick={() => analisar(l)}>Analisar</AsyncButton>
                          )}
                          {podeAprovar && <PrimaryButton type="button" onClick={() => abrirCadastro(l)}>Cadastrar</PrimaryButton>}
                          {podeAprovar && (
                            <SecondaryButton type="button" onClick={() => pedirRejeicao(l)}>Rejeitar</SecondaryButton>
                          )}
                          {(podeCriar || podeAprovar) && (
                            <AsyncButton variant="secondary" silentSuccess onClick={() => cancelarPedido(l)}>Cancelar</AsyncButton>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        )}
      </Card>

      {/* Cadastrar: o MESMO formulário do Cadastro de Suprimentos. */}
      {decidindo && (
        <FormularioProduto
          editando={editando}
          setEditando={setEditando}
          tiposCatalogo={tipos}
          tipoAtual={tipoAtual}
          onSalvar={salvarCadastro}
          onCancelar={fecharCadastro}
          titulo={`Cadastrar: ${decidindo.nome}`}
          aviso={(
            <InlineHint>
              O que a origem mandou é sugestão; vale o que você confirmar aqui. Preço é opcional: sem ele o material sai da fila com o selo "sem preço".
              {decidindo.sobras_vinculadas ? ` ${decidindo.sobras_vinculadas} sobra(s) aguardam este cadastro.` : ""}
              {vinculados?.items_count ? ` Usado em ${vinculados.items_count} item(ns) de ${vinculados.orcamentos_count} orçamento(s).` : ""}
            </InlineHint>
          )}
        />
      )}

      <SlideOverPanel
        open={Boolean(nova)}
        onClose={() => setNova(null)}
        title="Pedir cadastro de material"
        // Rodapé fixo do painel (auditoria mobile 2026-09-24): botões fora
        // do <form>, ligados pelo atributo `form`.
        footer={nova && (
          <>
            <SecondaryButton type="button" onClick={() => setNova(null)}>Cancelar</SecondaryButton>
            <PrimaryButton type="submit" form={FORM_PEDIR_ID}>Enviar pedido</PrimaryButton>
          </>
        )}
      >
        {nova && (
          <Form id={FORM_PEDIR_ID} onSubmit={handleNova}>
            <Field>Nome do material *<Input value={nova.nome} onChange={(e) => setNova((p) => ({ ...p, nome: e.target.value }))} maxLength={200} autoFocus /></Field>
            <FormGrid>
              <Field>
                Tipo
                <Select value={nova.tipo_material} onChange={(e) => setNova((p) => ({ ...p, tipo_material: e.target.value }))}>
                  <option value="CHAPA">Chapa</option><option value="FITA">Fita</option><option value="OUTRO">Outro</option>
                </Select>
              </Field>
              <Field>Código sugerido<Input value={nova.codigo_sugerido} onChange={(e) => setNova((p) => ({ ...p, codigo_sugerido: e.target.value }))} maxLength={40} /></Field>
            </FormGrid>
            <Field>Referência (código do fornecedor, se houver)<Input value={nova.chave_externa} onChange={(e) => setNova((p) => ({ ...p, chave_externa: e.target.value }))} maxLength={120} /></Field>
            {erro && <ErrorMsg><FiAlertCircle size={14} /> {erro}</ErrorMsg>}
          </Form>
        )}
      </SlideOverPanel>

      <ConfirmDialog
        open={Boolean(rejeitando)}
        titulo={`Rejeitar "${rejeitando?.nome}"?`}
        descricao={(
          <>
            {/* span: o ConfirmDialog renderiza a descrição dentro de <p>. */}
            <span style={{ display: "block" }}>{rejeitando?.sobras_vinculadas ? `${rejeitando.sobras_vinculadas} sobra(s) presa(s) a este material serão descartadas. ` : ""}O rascunho do produto some se ninguém o estiver usando.</span>
            <Textarea rows={2} maxLength={300} placeholder="Motivo (obrigatório)" value={motivo} onChange={(e) => setMotivo(e.target.value)} style={{ marginTop: 8, width: "100%" }} />
          </>
        )}
        confirmarLabel="Sim, rejeitar"
        cancelarLabel="Não"
        onConfirmar={confirmarRejeicao}
        onCancelar={() => { setRejeitando(null); setMotivo(""); }}
      />
    </>
  );
}
